import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Instagram, Linkedin, MessageCircle, Mail, MapPin } from 'lucide-react';

const LawWebsite = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: 'Negativas abusivas de cobertura por planos de saúde',
      description:
        'Atuamos de forma firme e estratégica contra negativas abusivas praticadas por planos de saúde, que frequentemente recusam procedimentos, exames, cirurgias, medicamentos ou internações sob justificativas ilegais. Nossa atuação envolve a análise minuciosa do contrato, do relatório médico e da legislação aplicável, identificando abusos como alegação de carência indevida, exclusão contratual genérica, ausência do procedimento no rol da ANS, dentre outros casos. Ingressamos com medidas administrativas e judiciais, inclusive com pedidos de tutela de urgência, para garantir o cumprimento imediato da cobertura. O objetivo é assegurar que o paciente não tenha seu tratamento interrompido ou inviabilizado por práticas abusivas, preservando sua saúde, dignidade e, muitas vezes, sua própria vida.',
      image:
        '/areas-atuacao-hq/img-sobre-nos-1.webp',
    },
    {
      title: 'Acesso a tratamentos, medicamentos e procedimentos de alto custo',
      description:
        'Defendemos o direito do paciente ao acesso integral ao tratamento prescrito pelo médico assistente, especialmente em casos que envolvem medicamentos de alto custo, terapias contínuas, tratamentos oncológicos, doenças raras, procedimentos inovadores ou tecnologias avançadas. Nossa atuação busca garantir que critérios financeiros jamais se sobreponham ao direito fundamental à saúde, utilizando fundamentos jurídicos e provas técnicas para assegurar o fornecimento imediato do tratamento necessário.',
      image:
        '/areas-atuacao-hq/img-sobre-nos-2.webp',
    },
    {
      title: 'Internações, cirurgias, UTI e home care',
      description:
        'Prestamos assessoria jurídica completa em situações que envolvem internações hospitalares, cirurgias de urgência ou eletivas, acesso à UTI e serviços de home care, frequentemente negados ou limitados por operadoras de saúde. Atuamos em casos de alta hospitalar precoce, negativa de UTI, limitação de dias de internação, recusa de cirurgia indicada ou suspensão indevida de home care. Nesses casos, adotamos medidas rápidas e eficazes, com pedidos liminares, para garantir a continuidade do cuidado e a segurança do paciente, evitando agravamento do quadro clínico e riscos à vida.',
      image:
        '/areas-atuacao-hq/img-sobre-nos-3.webp',
    },
    {
      title: 'Falhas na prestação de serviços médicos e hospitalares',
      description:
        'Atuamos na defesa de pacientes vítimas de falhas na prestação dos serviços de saúde, incluindo negligência, imprudência, imperícia, atrasos no atendimento, ausência de estrutura adequada, falhas em diagnósticos, infecções hospitalares e omissões no cuidado. Nosso trabalho consiste na apuração técnica e jurídica dos fatos, análise de prontuários médicos, laudos e documentos, com atuação voltada à responsabilização dos envolvidos e à reparação dos danos sofridos. Buscamos não apenas indenizações, mas também o reconhecimento da violação à dignidade e à integridade do paciente.',
      image:
        '/areas-atuacao-hq/img-sobre-nos-4.webp',
    },
    {
      title: 'Responsabilização de hospitais, clínicas e profissionais de saúde',
      description:
        'Promovemos ações de responsabilização civil de hospitais, clínicas e profissionais de saúde quando há comprovação de condutas ilícitas ou falhas que resultem em danos ao paciente. Atuamos em casos de erro médico, falhas estruturais, ausência de protocolos, atendimento inadequado ou desumanizado e condutas que coloquem o paciente em risco. Nosso foco é garantir que o paciente e sua família tenham seus direitos reconhecidos, recebam a devida reparação pelos danos materiais, morais e estéticos sofridos.',
      image:
        '/areas-atuacao-hq/img-sobre-nos-5.webp',
    },
    {
      title: 'Demandas administrativas e judiciais contra o SUS',
      description:
        'Defendemos pacientes em demandas contra o Sistema Único de Saúde (SUS), tanto na esfera administrativa quanto judicial, para garantir acesso a consultas, exames, cirurgias, medicamentos, internações, tratamentos especializados e vagas em UTI. Atuamos quando há omissão do Estado, demora excessiva, falta de vagas ou negativa injustificada de atendimento. Buscamos assegurar que o paciente receba o tratamento necessário em tempo adequado, sem que a burocracia ou a omissão estatal comprometam sua vida ou dignidade.',
      image:
        '/areas-atuacao-hq/img-sobre-nos-6.webp',
    },
  ];

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Sobre nós', id: 'sobre-nos' },
    { label: 'Áreas de atuação', id: 'areas-de-atuacao' },
    { label: 'Contato', id: 'contato' },
  ];

  const whatsappNumber = '5562981158364';
  const whatsappDisplay = '(62) 98115-8364';
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Olá. Gostaria de um atendimento jurídico. Poderiam me orientar?')}`;
  const linkedinUrl = 'https://www.linkedin.com/in/elys-marina-z-425358143/';
  const instagramUrl = 'https://www.instagram.com/elysmarinazenha/';
  const mailUrl = "mailto:elysmarina@zenha.adv.br";
  const logoSrc = '/logo192.png';

  const handleNavClick = (id) => (event) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header/Navigation */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-around items-center">
            <div className="flex items-center space-x-3">
              <div className="w-20 h-20 flex items-center justify-center overflow-hidden">
                <img
                  src={logoSrc}
                  alt="Zenha"
                  className={`w-12 h-12 object-contain transition duration-300 ${!scrolled ? 'invert brightness-0' : ''}`}
                />
              </div>
              <div>
                <div className={`font-bold text-lg ${scrolled ? 'text-gray-800' : 'text-white'}`}>ZENHA</div>
                <div className={`text-xs ${scrolled ? 'text-gray-500' : 'text-gray-100'}`}>Advocacia e Consultoria Jurídica</div>
              </div>
            </div>

            <nav className="hidden lg:flex space-x-8 items-center">
              {navItems.map((item) => (
                item.id !== 'contato' ? (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={handleNavClick(item.id)}
                    className={`transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-800' : 'text-white hover:text-gray-100'}`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <a href="#contato" onClick={handleNavClick('contato')} className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                    Fale conosco
                  </a>
                )
              ))}

            </nav>

            <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden">
              {menuOpen ? <X className={scrolled ? 'text-gray-800' : 'text-white'} /> : <Menu className={scrolled ? 'text-gray-800' : 'text-white'} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <div className="px-4 py-2 space-y-2">
              {navItems.map((item) => (
                item.id !== 'contato' ? (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(event) => {
                      handleNavClick(item.id)(event);
                      setMenuOpen(false);
                    }}
                    className="block py-2 text-gray-600 hover:text-gray-800"
                  >
                    {item.label}
                  </a>
                ) : (
                  <a href="#contato" className="block py-2 text-gray-600 hover:text-gray-800" onClick={() => setMenuOpen(false)}>
                    Fale conosco
                  </a>
                )
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: "url('/background-img-elys.png')", backgroundPosition: 'right -75px top' }}
        ></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-4">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Defesa do paciente na área da saúde
          </h1>
          <p className="text-xl md:text-2xl text-gray-50 mb-8 max-w-2xl mx-auto">
            Escritório especializado na defesa dos direitos dos pacientes, especialmente em demandas envolvendo planos de saúde, hospitais e prestadores de serviços médicos.
          </p>
          <a href="#sobre-nos" className="inline-flex items-center bg-white text-gray-800 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all transform hover:scale-105 shadow-xl">
            Quero saber mais
            <ChevronDown className="ml-2" />
          </a>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre-nos" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-gray-600 font-semibold uppercase tracking-wide text-sm">Sobre nós</span>
              <h2 className="text-4xl font-bold text-gray-800 mt-4 mb-6">
                Protegemos os direitos do paciente para que a dignidade, a vida e o acesso à saúde sejam efetivamente respeitados.
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Atuamos com propósito e responsabilidade na defesa dos direitos dos pacientes e de seus familiares, especialmente daqueles que se encontram em situação de vulnerabilidade diante de hospitais, clínicas, planos de saúde, prestadores de serviços médicos e do próprio Sistema Único de Saúde (SUS).
                </p>
                <p>
                  Nossa missão é clara e inegociável: proteger a vida, a dignidade e o acesso à saúde, assegurando que direitos fundamentais não sejam violados justamente nos momentos mais sensíveis e urgentes da vida.
                </p>
                <p>
                  Nosso diferencial vai além do conhecimento jurídico especializado. Ele está na forma como atuamos. Cada caso é conduzido de maneira individualizada, estratégica e humana, com absoluto comprometimento com resultados concretos e, sobretudo, com a realidade enfrentada pelo paciente e por sua família. Sabemos que, por trás de cada demanda, existe uma história, uma urgência e uma vida que não pode esperar.
                </p>
                <p>
                  A defesa do paciente não é apenas uma área de atuação — é a causa central do escritório. Todo o nosso empenho, estudo e atuação jurídica são direcionados à proteção da integridade física, emocional e moral do paciente, garantindo que seus direitos sejam respeitados e que o acesso à saúde jamais seja tratado como privilégio, mas como aquilo que verdadeiramente é: um direito fundamental.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="/elys-sobre-nos.jpeg"
                alt="Equipe jurídica"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gray-700 text-white p-6 rounded-lg shadow-xl">
                <div className="text-4xl font-bold">10+</div>
                <div className="text-sm">Anos de experiência</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="areas-de-atuacao" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gray-600 font-semibold uppercase tracking-wide text-sm">Áreas de Atuação</span>
          </div>

          <div className="space-y-12">
            {services.map((service, idx) => (
              <div
                key={idx}
                className={`grid md:grid-cols-2 gap-10 items-center bg-white rounded-2xl shadow-lg overflow-hidden ${idx % 2 === 1 ? 'md:[&>div:first-child]:order-2' : ''
                  }`}
              >
                <div className="h-72 md:h-full">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:p-12">
                  <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 leading-tight">
                    {service.title}
                  </h3>
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contato" className="bg-gradient-to-br from-gray-700 to-gray-800 text-gray-100 pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-4xl leading-tight font-light tracking-wide uppercase">
                Elys Marina
                <br />
                Zenha
              </h3>
              <div className='gap-12'>
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 mr-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 mr-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-3xl font-semibold mb-4">Entre em contato</h4>
              <div className="space-y-1 text-xl leading-relaxed text-gray-200">
                <a
                  href={whatsappUrl}
                  className="flex items-center gap-3 hover:text-white transition-colors w-fit"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
                    <MessageCircle size={18} />
                  </span>
                  <p>WhatsApp: {whatsappDisplay}</p>
                </a>

                <a
                  href={mailUrl}
                  className="flex items-center gap-3 hover:text-white transition-colors w-fit"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
                    <Mail size={18} />
                  </span>
                  elysmarina@zenha.adv.br
                </a>
                <div className="flex items-center gap-3">
                  <MapPin size={20} className='w-9' />
                  <p>Goiás, Brasil</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 border-t border-white/20 pt-8">
            <p className="text-center text-base text-gray-300">
              © {new Date().getFullYear()} – Elys Marina Zenha Marques – OAB/GO nº 39.706 • Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      <a
        href={whatsappUrl}
        aria-label="Falar no WhatsApp"
        className="fixed bottom-5 right-5 z-[60] inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#25D366]"
      >
        <svg
          viewBox="0 0 32 32"
          role="img"
          aria-hidden="true"
          className="h-9 w-9"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="16" cy="16" r="16" fill="#25D366" />
          <g transform="translate(16 16) scale(0.85) translate(-16 -16)">
            <path
              d="M16.04 2C8.28 2 2 8.28 2 16.04c0 2.82.73 5.46 2.01 7.78L2 30l6.37-1.68a13.96 13.96 0 007.67 2.22h.01C23.72 30.54 30 24.26 30 16.5 30 8.74 23.72 2.46 16.04 2zm8.11 20.31c-.34.96-2 1.84-2.75 1.96-.72.12-1.61.17-2.6.02-.6-.1-1.37-.31-2.35-.73-4.13-1.78-6.82-6.1-7.03-6.38-.2-.28-1.68-2.24-1.68-4.27 0-2.02 1.06-3.02 1.44-3.43.38-.4.83-.5 1.1-.5h.8c.26 0 .61-.1.95.74.35.85 1.2 2.94 1.3 3.15.1.2.17.44.03.72-.14.28-.2.44-.41.68-.2.24-.44.54-.63.72-.2.2-.4.4-.17.78.22.38.98 1.62 2.1 2.62 1.44 1.28 2.65 1.68 3.03 1.87.38.2.6.17.82-.1.22-.28.96-1.12 1.2-1.5.24-.38.5-.32.84-.2.34.12 2.2 1.04 2.57 1.23.38.2.63.28.72.44.1.15.1.9-.24 1.86z"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      </a>
    </div>
  );
};

export default LawWebsite;

